import React, { useState, useEffect, useRef } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const NoteNameGame = ({ gameData, onComplete }) => {
    const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null); 
    const [pressedKey, setPressedKey] = useState(null);
    const [shuffledNotes, setShuffledNotes] = useState([]);
    
    const audioCtxRef = useRef(null);

    useEffect(() => {
        if (gameData?.notes) {
            const shuffled = [...gameData.notes].sort(() => Math.random() - 0.5);
            setShuffledNotes(shuffled);
        }
        return () => {
            if (audioCtxRef.current) {
                audioCtxRef.current.close();
            }
        };
    }, [gameData]);

    const playNoteSound = (noteName) => {
        try {
            if (!audioCtxRef.current) {
                audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            const ctx = audioCtxRef.current;
            if (ctx.state === 'suspended') ctx.resume();

            const frequencies = {
                'C': 261.63, 'C#': 277.18,
                'D': 293.66, 'D#': 311.13,
                'E': 329.63,
                'F': 349.23, 'F#': 369.99,
                'G': 392.00, 'G#': 415.30,
                'A': 440.00, 'A#': 466.16,
                'B': 493.88,
                'C5': 523.25
            };

            let noteKey = noteName.replace(/[0-9]/g, '');
            if (noteName === 'C5') noteKey = 'C5';

            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.frequency.setValueAtTime(frequencies[noteKey] || 440, ctx.currentTime);
            osc.type = 'triangle';
            osc.connect(gain);
            gain.connect(ctx.destination);
            gain.gain.setValueAtTime(0.5, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 1.2);
            osc.start();
            osc.stop(ctx.currentTime + 1.2);
        } catch (e) {
            console.error("Audio error:", e);
        }
    };

    const handleKeyClick = (noteName) => {
        if (feedback) return;

        setPressedKey(noteName);
        playNoteSound(noteName);
        triggerHaptic(30);

        const currentNote = shuffledNotes[currentNoteIndex];
        const targetNote = currentNote.note.replace(/[0-9]/g, '');
        const isCorrect = noteName === targetNote;

        if (isCorrect) {
            const newScore = score + (gameData.points_per_note || 20);
            setScore(newScore);
            setFeedback({ status: 'correct', note: currentNote, key: noteName });
            triggerHaptic([100, 50, 100]);
            
            setTimeout(() => {
                setFeedback(null);
                setPressedKey(null);
                if (currentNoteIndex < shuffledNotes.length - 1) {
                    setCurrentNoteIndex(prev => prev + 1);
                } else {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                }
            }, 4000);
        } else {
            setFeedback({ status: 'wrong' });
            triggerHaptic([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
                setPressedKey(null);
            }, 1000);
        }
    };

    if (!gameData || !gameData.notes || shuffledNotes.length === 0) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-indigo-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🎼</div>
                <h3 className="text-base md:text-3xl font-black text-indigo-900 tracking-tighter uppercase">Derinami instrumentai...</h3>
            </div>
        );
    }

    const currentNote = shuffledNotes[currentNoteIndex];
    const progress = (currentNoteIndex / shuffledNotes.length) * 100;

    const getNotePosition = (position) => {
        const positions = {
            'line-1-below': 160, 
            'space-0-below': 150, 
            'line-1': 140, 
            'space-1': 130, 
            'line-2': 120, 
            'space-2': 110, 
            'line-3': 100, 
            'space-3': 90, 
        };
        return positions[position] || 100;
    };

    const keys = [
        { name: 'C', label: 'Do', color: 'white' },
        { name: 'D', label: 'Re', color: 'white' },
        { name: 'E', label: 'Mi', color: 'white' },
        { name: 'F', label: 'Fa', color: 'white' },
        { name: 'G', label: 'Sol', color: 'white' },
        { name: 'A', label: 'La', color: 'white' },
        { name: 'B', label: 'Si', color: 'white' },
    ];

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-indigo-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-indigo-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🎹
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Natų Maestro
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Meistriškumas</p>
                        <p className="text-sm md:text-2xl font-black text-indigo-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Nata</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentNoteIndex + 1}/{shuffledNotes.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-indigo-400 via-purple-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(99,102,241,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-[2rem] md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.03] select-none pointer-events-none">🎼</div>
                <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.5em] mb-4 md:mb-8">Penklinės Analizė</h4>
                
                <div className="w-full bg-slate-900 rounded-[2rem] md:rounded-[3rem] p-4 md:p-8 shadow-inner border-4 border-slate-800 relative min-h-[200px] md:min-h-[300px] flex items-center justify-center group overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.1)_0%,transparent_100%)] pointer-events-none" />
                    <div className="w-full max-w-2xl relative h-40 md:h-60 scale-75 md:scale-100">
                        {[0, 1, 2, 3, 4].map(i => (
                            <div key={i} className="absolute w-full h-1 bg-white/20 rounded-full" style={{ top: `${i * 24 + 48}px` }}></div>
                        ))}
                        <div className="absolute left-0 top-0 text-6xl md:text-9xl text-white/40 select-none opacity-90 animate-float">🎼</div>
                        <div
                            className={`absolute left-[55%] transform -translate-x-1/2 transition-all duration-1000 ease-out`}
                            style={{ top: `${getNotePosition(currentNote.position) - 20}px` }}
                        >
                            {currentNote.position === 'line-1-below' && (
                                <div className="absolute w-16 h-1.5 bg-white/40 top-[12px] left-1/2 -translate-x-1/2 rounded-full"></div>
                            )}
                            <div className="w-12 h-9 bg-gradient-to-br from-indigo-400 via-indigo-600 to-slate-900 rounded-[100%] shadow-2xl border-2 border-indigo-400 rotate-[-15deg] animate-pulse relative z-10" />
                            <div className="absolute w-1 h-24 bg-white/60 bottom-[4px] right-[4px] rounded-full" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-8 w-full max-w-5xl mx-auto py-4">
            <div className="relative">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-slate-800 text-white px-6 py-2 rounded-full text-[8px] font-black uppercase tracking-[0.4em] z-20 border border-slate-700">
                    Soliariumas
                </div>
                <div className="flex justify-center bg-slate-900 p-2 md:p-6 rounded-[2rem] md:rounded-[3rem] shadow-2xl relative z-10 gap-1 md:gap-3 py-6 md:py-12 border-t-8 border-slate-800">
                    {keys.map((key) => {
                        const isPressed = pressedKey === key.name;
                        const isWrong = feedback && feedback.status === 'wrong' && isPressed;

                        return (
                            <button
                                key={key.name}
                                onClick={() => handleKeyClick(key.name)}
                                className={`
                                    relative w-12 md:w-28 h-40 md:h-80 rounded-b-2xl md:rounded-b-[2rem] shadow-xl flex flex-col justify-end items-center pb-4 md:pb-12
                                    transition-all duration-150 outline-none select-none border-b-8 md:border-b-[16px] transform-gpu
                                    ${isPressed ? 'translate-y-4 border-b-0 brightness-90' : 'hover:-translate-y-1 active:translate-y-2'}
                                    bg-gradient-to-b from-white via-slate-50 to-slate-200 text-slate-800 border-slate-300
                                    ${isWrong ? '!bg-gradient-to-b !from-rose-500 !to-rose-600 !border-rose-800' : ''}
                                `}
                            >
                                <span className="text-sm md:text-4xl font-black mb-1 md:mb-3 drop-shadow-md">{key.label}</span>
                                <span className="text-[8px] md:text-[10px] font-black opacity-30 uppercase tracking-widest">{key.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && feedback.status === 'correct' && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className="max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[5rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px] border-indigo-500 text-center">
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            🎻
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className="text-2xl md:text-5xl font-black uppercase tracking-tighter text-indigo-600 leading-none">
                                TOBULA KLAUSA!
                            </h2>

                            <div className="p-6 md:p-10 rounded-2xl bg-indigo-50 border-2 border-indigo-100 shadow-inner">
                                <div className="space-y-2 mb-6">
                                    <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.5em]">Atpažinta Nata</p>
                                    <span className="text-4xl md:text-8xl font-black text-indigo-950 block leading-none italic">
                                        {feedback.note?.note}
                                    </span>
                                </div>
                                <p className="text-sm md:text-xl font-bold text-slate-700 leading-relaxed font-serif italic">
                                    "Puiku! Tai nata <span className="text-indigo-600">{feedback.note?.note}</span> ({keys.find(k => k.name === feedback.note?.note.replace(/[0-9]/g, ''))?.label})!"
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default NoteNameGame;
